import { ChangeDetectionStrategy, Component, computed, forwardRef, input, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** A single selectable combobox option. */
export interface ComboboxOptionI<T = string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

let comboboxIdCounter = 0;
const nextComboboxId = (): string => `bx-combobox-${++comboboxIdCounter}`;

/**
 * Combobox (#1142) — a standalone, OnPush typeahead/select implementing
 * {@link ControlValueAccessor} so it drops into reactive forms (`[formControl]` /
 * `formControlName`) exactly like a native control.
 *
 * Built for the assessment wizard (#1156) where a long list of options needs to be
 * filterable by typing while remaining fully keyboard- and screen-reader-navigable.
 *
 * Keyboard / a11y (WAI-ARIA combobox pattern):
 *  - `role="combobox"` text input with `aria-expanded`, `aria-controls`,
 *    `aria-activedescendant` pointing at the highlighted option.
 *  - ArrowDown / ArrowUp move the active descendant (and open the popup).
 *  - Enter commits the active option; Escape closes without committing.
 *  - Each option is `role="option"` with `aria-selected` on the chosen value.
 *
 * Token-driven styling only (no Bootstrap). Presentational filtering: the host owns
 * the option list; this component only filters the provided options by label.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'bodyanalytics-combobox',
  standalone: true,
  templateUrl: './combobox.component.html',
  styleUrls: ['./combobox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboboxComponent),
      multi: true,
    },
  ],
})
export class ComboboxComponent<T extends string | number = string> implements ControlValueAccessor {
  readonly label = input.required<string>();
  readonly options = input.required<ComboboxOptionI<T>[]>();
  readonly placeholder = input<string>('Search…');
  readonly required = input<boolean>(false);
  readonly hint = input<string | undefined>(undefined);
  readonly error = input<string | undefined>(undefined);
  readonly inputId = input<string>(nextComboboxId());

  /** Selected value, mirrored from the FormControl via `writeValue`. */
  protected readonly value = signal<T | null>(null);
  /** Current text in the input — drives the typeahead filter. */
  protected readonly query = signal<string>('');
  protected readonly open = signal<boolean>(false);
  protected readonly disabled = signal<boolean>(false);
  /** Index into {@link filteredOptions} of the keyboard-highlighted option. */
  protected readonly activeIndex = signal<number>(-1);

  protected readonly listId = computed(() => `${this.inputId()}-listbox`);
  protected readonly errorId = computed(() => `${this.inputId()}-error`);
  protected readonly hintId = computed(() => `${this.inputId()}-hint`);
  protected readonly invalid = computed(() => !!this.error());

  protected readonly describedBy = computed(() => {
    const ids: string[] = [];
    if (this.hint()) ids.push(this.hintId());
    if (this.error()) ids.push(this.errorId());
    return ids.length ? ids.join(' ') : null;
  });

  /** Options whose label matches the current query (case-insensitive substring). */
  protected readonly filteredOptions = computed<ComboboxOptionI<T>[]>(() => {
    const q = this.query().trim().toLowerCase();
    const opts = this.options();
    if (!q) return opts;
    return opts.filter(o => o.label.toLowerCase().includes(q));
  });

  protected readonly activeDescendantId = computed<string | null>(() => {
    const idx = this.activeIndex();
    const opts = this.filteredOptions();
    if (idx < 0 || idx >= opts.length) return null;
    return this.optionId(idx);
  });

  /** Label shown in the closed input for the currently selected value. */
  protected readonly selectedLabel = computed<string>(() => {
    const v = this.value();
    if (v === null) return '';
    return this.options().find(o => o.value === v)?.label ?? '';
  });

  private onChange: (value: T | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  // ── ControlValueAccessor ──────────────────────────────────────────────────
  writeValue(value: T | null): void {
    this.value.set(value);
    this.query.set(value === null ? '' : (this.options().find(o => o.value === value)?.label ?? ''));
  }
  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected optionId(index: number): string {
    return `${this.inputId()}-opt-${index}`;
  }

  protected isSelected(option: ComboboxOptionI<T>): boolean {
    return this.value() !== null && option.value === this.value();
  }

  protected handleInput(event: Event): void {
    if (this.disabled()) return;
    const target = event.target as HTMLInputElement;
    this.query.set(target.value);
    this.open.set(true);
    // Reset highlight to the first match whenever the filter changes.
    this.activeIndex.set(this.filteredOptions().length ? 0 : -1);
  }

  protected handleFocus(): void {
    if (this.disabled()) return;
    this.open.set(true);
  }

  protected handleBlur(): void {
    // Defer close so an option mousedown commits before the popup tears down.
    this.open.set(false);
    this.onTouched();
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    const opts = this.filteredOptions();

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        if (!this.open()) {
          this.open.set(true);
        }
        if (opts.length) {
          const next = this.activeIndex() < opts.length - 1 ? this.activeIndex() + 1 : 0;
          this.activeIndex.set(next);
        }
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        if (!this.open()) {
          this.open.set(true);
        }
        if (opts.length) {
          const prev = this.activeIndex() > 0 ? this.activeIndex() - 1 : opts.length - 1;
          this.activeIndex.set(prev);
        }
        break;
      }
      case 'Enter': {
        if (this.open() && this.activeIndex() >= 0 && this.activeIndex() < opts.length) {
          event.preventDefault();
          this.commit(opts[this.activeIndex()]);
        }
        break;
      }
      case 'Escape': {
        if (this.open()) {
          event.preventDefault();
          this.open.set(false);
          this.activeIndex.set(-1);
        }
        break;
      }
      default:
        break;
    }
  }

  /** Commit a chosen option: sync the input text, notify the form, close the popup. */
  protected selectOption(option: ComboboxOptionI<T>): void {
    if (option.disabled || this.disabled()) return;
    this.commit(option);
  }

  private commit(option: ComboboxOptionI<T>): void {
    if (option.disabled) return;
    this.value.set(option.value);
    this.query.set(option.label);
    this.open.set(false);
    this.activeIndex.set(-1);
    this.onChange(option.value);
    this.onTouched();
  }
}
